import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchActivityStatus } from '../../hooks/useFetchActivityStatus';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import NoData from '../dashboard/noData';
import { GeneralEmpty, Slash } from '../icons';
import { usePage } from '../../context/PageContext';
import { useTask } from '../../context/TaskContext';
import { useLanguage } from '../../context/LanguageContext';
import { ITranslation } from '../../interface/transaction';
import { ITask } from '../../interface/tasks';
import { Radio, RadioGroup, Spin, Text } from '../Ant';
import { useNavigationStack } from '../../context/NavigationStackContext';
import { useEscapeToGoBack } from '../../hooks/useEscapeToGoBack';
import SkeletonOptionList from '../common/skeleton/skeletonOptionList';
import CollapsibleListLayout from '../collapsibleListLayout/collapsibleListLayout';
import SearchInputWithFilters from '../dashboard/SearchInputWithFilters';
import { useDiscardDialog } from '../../hooks/useDiscardDialog';
import { isTaskFormChanged } from './isTaskFormChanged';
import { isCriticalTaskFormChanged } from '../criticalTask/isCriticalTaskFormChanged';
import CustomModal from '../informationPopup/customModal';

const TaskStatuses = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { task, updateTask, initialTask, clearInitialTask } = useTask();
  const { setTitle } = usePage();
  const { goBack, goBackWithSelection } = useNavigationStack();
  const { getAppLanguageCode } = useLanguage();
  const isCriticalTaskRoute = location?.state?.fromCritical ?? false;
  const { showDiscardDialog, promptDiscardIfChanged, handleDiscardConfirm, handleDiscardCancel } =
    useDiscardDialog();
  const [searchValue, setSearchValue] = useState<string>('');
  const {
    data: statusList,
    isLoading: isActivityStatusLoading,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useFetchActivityStatus({ pageSize: 10, search: searchValue });

  const handleBack = () => {
    goBack();
  };
  useEscapeToGoBack(handleBack);
  const handleClose = () => {
    const isFormChanged = isCriticalTaskRoute
      ? isCriticalTaskFormChanged(task, initialTask, !!task?.id)
      : isTaskFormChanged(task, initialTask, !!task?.id);
    promptDiscardIfChanged(isFormChanged, () => {
      updateTask(null);
      clearInitialTask();
      goBack(undefined, 2);
    });
  };

  const refreshStatusList = () => {
    queryClient.removeQueries({ queryKey: ['activity-status-list'] });
    return refetch();
  };

  const { ref, inView } = useInView({
    threshold: 0.5, // Trigger when 50% of the target is visible
  });

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && statusList?.length > 0) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage]);
  useEffect(() => {
    setTitle(
      <AddEditTransactionHeader
        handleBack={handleBack}
        handleClose={handleClose}
        showBackWithCross
        title={t('task:selectStatus')}
        showCross
      />,
    );
  }, []);
  const handleSelectStatus = (value: string) => {
    updateTask({
      ...task,
      statusId: value,
      status: statusList?.find((status) => status.id === value),
    } as ITask);
    goBackWithSelection();
  };
  const renderLoader = () => {
    if (isFetching) {
      return <Spin size="small" data-testid="small-indicator" />;
    }
    return null;
  };

  return (
    <>
      <CollapsibleListLayout
        testId="task-status-selection"
        header={
          <SearchInputWithFilters
            searchTitle={t('task:searchStatuses')}
            handleSearch={setSearchValue}
            handleRefresh={refreshStatusList}
          />
        }
      >
        {statusList && statusList.length > 0 && (
          <RadioGroup
            data-testid="task-status-radio-group"
            className="d-flex fd-column gap-10 overflow-y-auto"
            value={task?.status?.id ?? null}
          >
            {statusList.map((status) => {
              const translation = status?.translations?.find(
                (t: ITranslation) => t.language === getAppLanguageCode(),
              );

              return (
                <Radio
                  data-testid={`task-status-radio-${translation?.translation}`}
                  key={status?.id}
                  value={status?.id}
                  onClick={() => handleSelectStatus(status?.id)}
                >
                  <Text
                    data-testid={`task-status-text-${translation?.translation}`}
                    $fontsize="13px"
                    $weight={500}
                    $color="title"
                    $lineheight="20px"
                  >
                    {translation?.translation ?? '-'}
                  </Text>
                </Radio>
              );
            })}
          </RadioGroup>
        )}
        {hasNextPage && <div ref={ref} className="loading-indicator" />}
        {(!statusList || (Object.entries(statusList).length === 0 && !isActivityStatusLoading)) && (
          <div className="h-full d-flex justify-center items-center">
            <NoData
              data-testid="no-data"
              header={t('common:noData')}
              subHeader={t('common:tryChangingSearchCriteria')}
              icon={<GeneralEmpty />}
            />
          </div>
        )}
        {isActivityStatusLoading ? <SkeletonOptionList /> : renderLoader()}
      </CollapsibleListLayout>
      <CustomModal
        visible={showDiscardDialog}
        onConfirm={handleDiscardConfirm}
        icon={<Slash />}
        title={t('common:discardChangesText')}
        subdescription={t('common:anyChangeMadeWillBeLostAndCannotBeReverted')}
        confirmText={t('common:discard')}
        cancelText={t('common:cancel')}
        onClose={handleDiscardCancel}
      />
    </>
  );
};

export default TaskStatuses;
